import {
  Button, Group, Text, ActionIcon, Modal, ScrollArea, type MantineStyleProp, Tooltip, Stack, useMantineTheme
} from '@mantine/core';

import { useState, useCallback, useMemo } from 'react';

import { useDisclosure } from '@mantine/hooks';

import { useParams } from 'react-router-dom';

import { useDispatch } from 'react-redux';

import EditOutlineDnD from './EditOutlineDnd';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import { type SectionData } from '../../Pages/Editor/SectionEditor';
import { AddRequirement, AddSection, ExtractOutline, ResetOutline } from '../Modals';

import { defaultModalProps } from '../../utils/mantineUtils';

import CenteredLoader from '../CenteredLoader';
import { type ProposalSection, type Proposal, type WithoutPermissions } from '../../types/apiTypes';
import { AddSection as AddSectionIcon, CreationSparkles, RestoreIcon, SplitView } from '../../icons';
import BulkRequirementButtonGroup from '../BulkRequirementButtonGroup';
import { formatSectionDataForRedux } from '../../utils/dndUtils';

import {
  addSection,
  resetOutline as resetOutlineState,
  updateOutlineRequirements,
  updateOutlineSections
} from '../../redux/OutlineSlice';
import DeleteRequirements from '../Modals/DeleteRequirements';
import { type RequirementType, type RequirementRemovalType, formatRequirementData } from '../../utils/requirementUtils';
import OutlineDisplayOptions from '../DropdownMenus/OutlineDisplayOptions';
import { OpportunityDocumentsViewer } from '../OpportunityDocumentsViewer';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import { formatSectionsMetadata } from '../../utils/apiUtils';

interface Props {
  proposal: Proposal
  sections: SectionData[]
  requirementRemovalType: RequirementRemovalType
  isSetup?: boolean
  style?: MantineStyleProp
  onStartAuthoring?: () => void
}

const ProposalOutlineWithDocumentViewer: React.FC<Props> = (props: Props) => {
  const { proposal, sections, style, requirementRemovalType, isSetup = false, onStartAuthoring } = props;
  const { proposalUid } = useParams();
  const [focusedRequirementUid, setFocusedRequirementUid] = useState('');
  const [showDocumentsViewer, setShowDocumentsViewer] = useState(true);
  const [showAssignedUsers, setShowAssignedUsers] = useState(false);
  const [showRequirements, setShowRequirements] = useState(true);
  const [parentSectionUid, setParentSectionUid] = useState<string | undefined>(undefined);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [opportunityFileToAddTo, setOpportunityFileToAddTo] = useState<string>('');
  const [defaultRequirementText, setDefaultRequirementText] = useState<string>('');
  const [addSectionOpened, addSectionHandlers] = useDisclosure();
  const [addRequirementOpened, addRequirementHandlers] = useDisclosure();
  const [extractOutlineOpened, extractOutlineHandlers] = useDisclosure();
  const [deleteRequirementsOpened, deleteRequirementsHandlers] = useDisclosure();
  const [resetOutlineOpened, resetOutlineHandlers] = useDisclosure();
  const [checkedRequirements, setCheckedRequirements] = useState<RequirementType[]>([]);
  const dispatch = useDispatch();
  const { other: colorVars } = useMantineTheme();

  const { data: requirementResponseData, isLoading: requirementResponseLoading } = useEnsisQuery(
    '/app/requirement-responses',
    {
      queryParams: { proposal_uid: proposalUid },
      gcTime: 0
    }
  );

  const sectionWidthOffest = isSetup ? '100px' : '118px';
  const hasDefaultOrgOutline = (proposal?.default_outline_sections !== undefined &&
    proposal?.default_outline_sections !== null);
  const showDefaultOrgOutlineActions = hasDefaultOrgOutline && isSetup;
  const inBulkEditMode = checkedRequirements.length > 0;

  const requirementsToHighlight = useMemo(() => {
    if (isSetup) {
      return requirementResponseData?.items?.filter((req) => req?.proposal_section !== null) ?? [];
    }
    return requirementResponseData?.items ?? [];
  }, [requirementResponseData?.items]);

  const handleSetOutlineSuccess = useCallback((data: Array<WithoutPermissions<ProposalSection>>) => {
    const sections = formatSectionsMetadata(data ?? []);
    const requirements = formatRequirementData(requirementResponseData?.items ?? []);
    dispatch(resetOutlineState());
    dispatch(updateOutlineSections({ sections }));
    dispatch(updateOutlineRequirements({ requirements }));
  }, [requirementResponseData?.items]);

  const setOutlineMutation = useEnsisMutation(`/app/proposals/${proposalUid}/set-outline`, {
    requestType: 'patch',
    awaitRefetch: true,
    showSuccessMessage: false,
    queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`, '/app/requirement-responses'],
    onSuccess: handleSetOutlineSuccess
  });

  const extractOutlineMutation = useEnsisMutation(`/app/proposals/${proposalUid}/generate-outline`, {
    requestType: 'post',
    queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`],
    showSuccessMessage: false
  });

  const handleOpenExtractOutline = useCallback(() => {
    extractOutlineMutation.mutate({});
    extractOutlineHandlers.open();
  }, [extractOutlineMutation]);

  const onOpenAddRequirement = useCallback((requirementText?: string, opportunityFileUid?: string) => {
    setOpportunityFileToAddTo(opportunityFileUid ?? '');
    setDefaultRequirementText(requirementText ?? '');
    addRequirementHandlers.open();
  }, []);

  const toggleFocusedRequirement = useCallback((requirementUid: string) => {
    if (focusedRequirementUid === requirementUid) {
      setFocusedRequirementUid('');
    } else {
      setFocusedRequirementUid(requirementUid);
    }
  }, [focusedRequirementUid]);

  const handleCheckRequirement = useCallback((req: RequirementType) => {
    if (checkedRequirements.some((_req) => _req.requirementUid === req.requirementUid)) {
      setCheckedRequirements(checkedRequirements.filter((_req) => _req !== req));
    } else {
      setCheckedRequirements([...checkedRequirements, req]);
    }
  }, [checkedRequirements]);

  const onOpenAddSection = useCallback((parentSectionUid?: string) => {
    setParentSectionUid(parentSectionUid);
    addSectionHandlers.open();
  }, []);

  const onAddSectionSuccess = useCallback((data: any) => {
    const formattedData = formatSectionDataForRedux(data);
    dispatch(addSection(formattedData));
  }, []);

  const onClickResetOutline = useCallback(() => {
    setOutlineMutation.mutate({}, {
      onSuccess: resetOutlineHandlers.close
    });
  }, [setOutlineMutation]);
  const resetOutlineButton = (
    <Button leftSection={<RestoreIcon />} variant='subtle' onClick={resetOutlineHandlers.open}>
      Reset Outline
    </Button>);

  const extractOutlineButton = (
    <Button leftSection={<CreationSparkles />} variant='subtle' onClick={handleOpenExtractOutline}>
      Extract Outline
    </Button>);

  const addSectionIconOnly = (
    <Tooltip label='Add Section' >
      <ActionIcon
        variant='subtle'
        onClick={() => { onOpenAddSection(undefined); }}
        size={36}
        c={colorVars.primaryIconColor}
      >
        <AddSectionIcon />
      </ActionIcon>
    </Tooltip>
  );

  const addSectionFullButton = (
    <Button leftSection={<AddSectionIcon />} variant='subtle' onClick={() => { onOpenAddSection(undefined); }}>
      Add Section
    </Button>
  );

  const addSectionButton = showDefaultOrgOutlineActions ? addSectionIconOnly : addSectionFullButton;
  const toggleDocumentsViewerButton = (
    <Tooltip
      label={showDocumentsViewer ? 'Expand' : 'Collapse'}
    >
      <ActionIcon
        size={36}
        variant='subtle'
        c={colorVars.primaryIconColor}
        onClick={() => { setShowDocumentsViewer(!showDocumentsViewer); }}
      >
        <SplitView />
      </ActionIcon>
    </Tooltip>
  );
  const actionButtons = (
    <Group justify='end' gap={2}>
      {showDefaultOrgOutlineActions && resetOutlineButton}
      {showDefaultOrgOutlineActions && extractOutlineButton}
      {addSectionButton}
      <OutlineDisplayOptions
        iconOnly={showDefaultOrgOutlineActions}
        showRequirementsSelected={showRequirements}
        toggleShowRequirements={() => { setShowRequirements(!showRequirements); }}
        showUsersSelected={showAssignedUsers}
        toggleShowUsers={() => { setShowAssignedUsers(!showAssignedUsers); }}
      />
      {toggleDocumentsViewerButton}
    </Group>
  );

  if (requirementResponseLoading) {
    return <CenteredLoader />;
  }
  return (
    <>
      <Modal opened={extractOutlineOpened} {...defaultModalProps}>
        <ExtractOutline
          loading={extractOutlineMutation.isPending}
          outline={extractOutlineMutation?.data ?? []}
          onClose={extractOutlineHandlers.close}
          onSetOutlineSuccess={handleSetOutlineSuccess}
        />
      </Modal>
      <Modal opened={addSectionOpened} {...defaultModalProps}>
        <AddSection
          parentSectionUid={parentSectionUid}
          close={addSectionHandlers.close}
          sections={sections}
          onAddSectionSuccess={onAddSectionSuccess}
        />
      </Modal>
      <Modal opened={addRequirementOpened} {...defaultModalProps}>
        <AddRequirement
          updateReduxOnSuccess
          opportunityUid={proposal?.opportunity?.uid ?? ''}
          defaultRequirementText={defaultRequirementText}
          close={addRequirementHandlers.close}
          opportunityFileUid={opportunityFileToAddTo}
        />
      </Modal>
      <Modal opened={deleteRequirementsOpened} {...defaultModalProps}>
        <DeleteRequirements
          opportunityUid={proposal.opportunity?.uid ?? ''}
          requirements={checkedRequirements}
          onDeleteSuccess={() => { setCheckedRequirements([]); }}
          close={deleteRequirementsHandlers.close}
        />
      </Modal>
      <Modal opened={resetOutlineOpened} {...defaultModalProps}>
        <ResetOutline
          onClick={onClickResetOutline}
          onClose={resetOutlineHandlers.close}
          loading={setOutlineMutation.isPending}
        />
      </Modal>
      <Group gap={0} align='stretch' mih={400} style={{ ...style }}>
        <Stack gap={0} flex={1} p={16}>
          <Group justify='space-between' pb={16}>
            <Text fz={18}>
              Outline
            </Text>
            {inBulkEditMode
              ? <BulkRequirementButtonGroup
                moveButtonCopy='Move'
                requirementRemovalType={requirementRemovalType}
                checkedRequirements={checkedRequirements}
                sections={sections}
                onClearCheckedRequirements={() => { setCheckedRequirements([]); }}
                onOpenDeleteRequirements={deleteRequirementsHandlers.open}
              />
              : actionButtons
            }
          </Group>
          <ScrollArea flex={1}>
            <EditOutlineDnD
              opportunityUid={proposal.opportunity?.uid ?? ''}
              showRequirements={showRequirements}
              onRowClick={toggleFocusedRequirement}
              focusedRequirementUid={focusedRequirementUid}
              sectionWidth={showDocumentsViewer ? 'calc(50vw - 100px)' : `calc(100vw - ${sectionWidthOffest})`}
              sections={sections}
              requirementResponses={requirementResponseData?.items ?? []}
              onOpenAddSection={onOpenAddSection}
              checkedRequirements={checkedRequirements}
              onCheckRequirement={handleCheckRequirement}
              showAssignedUsers={showAssignedUsers}
              requirementRemovalType={requirementRemovalType}
            />
          </ScrollArea>
        </Stack>
        {showDocumentsViewer &&
          <OpportunityDocumentsViewer
            // Special case where we have to use calc because the PDF viewer styling is finicky
            style={{ flex: 1, height: 'calc(100% - 42px)' }}
            proposal={proposal}
            currentFileIndex={currentFileIndex}
            onClickFile={(index: number) => { setCurrentFileIndex(index); }}
            requirementResponses={requirementsToHighlight}
            focusedRequirementUid={focusedRequirementUid}
            onClickHighlightedRequirement={(reqUid: string) => { toggleFocusedRequirement(reqUid); }}
            onAddRequirement={isSetup ? undefined : onOpenAddRequirement}
          />
        }
      </Group>
      {isSetup && <Group pr={16} pb={16} justify='end' w={showDocumentsViewer ? '50%' : '100%'}>
        <Button onClick={onStartAuthoring}>
          Start Authoring
        </Button>
      </Group>}
    </>
  );
};

export default ProposalOutlineWithDocumentViewer;
